import { useState } from 'react';
import { Link, useNavigate } from 'react-router';
import { useStore, useTranslation } from '../store/useStore';
import { Shield, Eye, EyeOff, Mail, Lock } from 'lucide-react';

export default function Login() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuth } = useStore();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    // Demo authentication
    const demoUsers: Record<string, { id: number; firstName: string; lastName: string; email: string; role: string }> = {
      'admin@ipo.com': { id: 1, firstName: 'System', lastName: 'Admin', email: 'admin@ipo.com', role: 'super_admin' },
      'buyer@ipo.com': { id: 2, firstName: 'John', lastName: 'Doe', email: 'buyer@ipo.com', role: 'buyer' },
      'supplier@ipo.com': { id: 3, firstName: 'Maria', lastName: 'Garcia', email: 'supplier@ipo.com', role: 'supplier' },
      'affiliate@ipo.com': { id: 4, firstName: 'Chen', lastName: 'Wei', email: 'affiliate@ipo.com', role: 'member' },
      'ipme@ipo.com': { id: 5, firstName: 'Amina', lastName: 'Osei', email: 'ipme@ipo.com', role: 'member' },
    };

    setTimeout(() => {
      const demoUser = demoUsers[email];
      if (demoUser && password.length >= 4) {
        setAuth(
          {
            id: demoUser.id,
            email: demoUser.email,
            firstName: demoUser.firstName,
            lastName: demoUser.lastName,
            role: demoUser.role,
            status: 'active',
            kycStatus: 'verified',
            preferredLanguage: 'en',
            preferredCurrency: 'USD',
            foundingMember: demoUser.id === 4,
            ipmeStatus: demoUser.id === 5 ? 'active' : 'none',
          },
          'demo_token_' + demoUser.id
        );
        navigate('/');
      } else {
        setError('Invalid email or password. Try demo accounts: admin@ipo.com, buyer@ipo.com, supplier@ipo.com');
      }
      setLoading(false);
    }, 500);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-[#f8fafc] via-[#e0e7ff]/20 to-[#fed7aa]/20 pt-20">
      <div className="w-full max-w-md px-4">
        <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-8">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-14 h-14 mx-auto mb-4 bg-gradient-hero rounded-2xl flex items-center justify-center">
              <Shield className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-semibold text-[#1e293b] font-display">{t('auth.login')}</h1>
            <p className="text-sm text-[#64748b] mt-1">Welcome back to IPO</p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.email')}</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full pl-10 pr-4 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#1e293b] mb-1.5">{t('auth.password')}</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#94a3b8]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full pl-10 pr-10 py-3 rounded-xl border border-gray-200 focus:border-[#c2410c] focus:ring-2 focus:ring-[#c2410c]/20 outline-none text-sm"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8] hover:text-[#64748b]"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-[#64748b]">
                <input type="checkbox" className="rounded border-gray-300" />
                Remember me
              </label>
              <Link to="#" className="text-sm text-[#c2410c] hover:underline">{t('auth.forgot')}</Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3.5 bg-[#c2410c] text-white rounded-xl hover:bg-[#9a3412] transition-colors text-sm font-semibold disabled:opacity-50"
            >
              {loading ? 'Signing in...' : t('auth.login')}
            </button>
          </form>

          {/* Demo Accounts */}
          <div className="mt-6 p-4 bg-[#f8fafc] rounded-xl">
            <p className="text-xs font-semibold text-[#64748b] mb-2 uppercase tracking-wider">Demo Accounts</p>
            <div className="space-y-1.5">
              {['admin@ipo.com', 'buyer@ipo.com', 'supplier@ipo.com', 'affiliate@ipo.com', 'ipme@ipo.com'].map((email) => (
                <button
                  key={email}
                  onClick={() => setEmail(email)}
                  className="block w-full text-left text-xs text-[#4338ca] hover:text-[#c2410c] py-1 transition-colors"
                >
                  {email}
                </button>
              ))}
            </div>
            <p className="text-[10px] text-[#94a3b8] mt-2">Any password with 4+ characters works for demo</p>
          </div>

          <p className="mt-6 text-center text-sm text-[#64748b]">
            {t('auth.no_account')}{' '}
            <Link to="/register" className="text-[#c2410c] font-medium hover:underline">{t('auth.register_now')}</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
